import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Linkedin, Youtube, ArrowUpRight, MapPin, Phone, Mail } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { FadeUp, Stagger, staggerItem } from "../components/Reveal";
import { motion } from "framer-motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REGIONS = [
  { name: "North America", email: "northamerica@aureliusdynamics.com", phone: "+1 (713) 555-0142", code: "NA" },
  { name: "Europe", email: "europe@aureliusdynamics.com", phone: "+44 20 7946 0821", code: "EU" },
  { name: "Asia", email: "asia@aureliusdynamics.com", phone: "+81 3 6890 3155", code: "AS" },
];

const ENQUIRIES = [
  { title: "Business Development", email: "partnerships@aureliusdynamics.com" },
  { title: "Media Enquiries", email: "media@aureliusdynamics.com" },
  { title: "Career Enquiries", email: "careers@aureliusdynamics.com" },
  { title: "General Corporate", email: "contact@aureliusdynamics.com" },
];

function ContactHQ() {
  return (
    <section className="py-24 md:py-32" data-testid="contact-hq">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-4">
          <p className="eyebrow-gold">§ 01 — Global Headquarters</p>
          <FadeUp><h2 className="mt-6 h-section text-4xl md:text-5xl text-[var(--navy)]">Houston, Texas.</h2></FadeUp>
        </div>
        <div className="md:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)]">
          <div className="bg-white p-8">
            <div className="flex items-start gap-3">
              <MapPin size={16} className="text-[var(--gold-soft)] mt-1" />
              <div>
                <p className="eyebrow mb-2">Address</p>
                <p className="font-serif text-2xl text-[var(--navy)] leading-tight">
                  1200 Innovation Parkway<br />
                  Houston, Texas 77002<br />
                  United States
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-8 flex flex-col gap-6">
            <div className="flex items-start gap-3">
              <Phone size={16} className="text-[var(--gold-soft)] mt-1" />
              <div>
                <p className="eyebrow mb-2">Phone</p>
                <p className="font-serif text-2xl text-[var(--navy)]">+1 (713) 555-0186</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Mail size={16} className="text-[var(--gold-soft)] mt-1" />
              <div>
                <p className="eyebrow mb-2">Email</p>
                <a href="mailto:contact@aureliusdynamics.com" data-testid="contact-hq-email" className="font-serif text-2xl text-[var(--navy)] link-underline">contact@aureliusdynamics.com</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function RegionalContacts() {
  return (
    <section className="py-16 md:py-24 bg-[var(--paper)]" data-testid="contact-regions">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
          <div className="md:col-span-4"><p className="eyebrow-gold">§ 02 — Regional Contacts</p></div>
          <div className="md:col-span-8">
            <FadeUp>
              <h2 className="h-section text-4xl md:text-5xl text-[var(--navy)]">Three regions.<br /><span className="italic">One organisation.</span></h2>
            </FadeUp>
          </div>
        </div>
        <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--line)] border border-[var(--line)]">
          {REGIONS.map((r) => (
            <motion.div key={r.name} variants={staggerItem} className="bg-white p-10 min-h-[260px] flex flex-col justify-between" data-testid={`region-${r.code.toLowerCase()}`}>
              <div>
                <p className="font-mono text-xs text-[var(--gold-soft)] tracking-[0.24em]">REGION · {r.code}</p>
                <h3 className="mt-4 font-serif text-4xl text-[var(--navy)]">{r.name}</h3>
              </div>
              <div className="space-y-2 pt-6 border-t border-[var(--line)]">
                <a href={`mailto:${r.email}`} className="block text-[var(--navy)] link-underline text-sm">{r.email}</a>
                <p className="text-[var(--steel)] text-sm font-mono">{r.phone}</p>
              </div>
            </motion.div>
          ))}
        </Stagger>
      </div>
    </section>
  );
}

function ContactForm() {
  const [form, setForm] = useState({
    full_name: "", organization: "", email: "", phone: "", country: "", subject: "", message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.subject || !form.message) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Your enquiry has been received. Our team will respond shortly.");
      setForm({ full_name: "", organization: "", email: "", phone: "", country: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "w-full bg-transparent border-0 border-b border-[var(--line)] py-4 px-0 text-[var(--navy)] placeholder:text-[var(--steel)] font-light text-base focus:border-[var(--navy)] transition-colors duration-300";

  return (
    <section className="py-24 md:py-32" data-testid="contact-form-section">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-16">
        <div className="md:col-span-4">
          <p className="eyebrow-gold">§ 03 — Begin a Conversation</p>
          <FadeUp>
            <h2 className="mt-6 h-section text-4xl md:text-5xl text-[var(--navy)]">
              Tell us about your <span className="italic">program.</span>
            </h2>
            <p className="mt-6 text-[var(--steel)] leading-relaxed">
              Our team responds to every enquiry personally, typically within one to two business days.
            </p>
          </FadeUp>

          <div className="mt-10 border-t border-[var(--line)] pt-8">
            <p className="eyebrow mb-4">Office Hours</p>
            <p className="text-[var(--navy)] font-serif text-xl">Mon — Fri · 08:00 – 18:00 CT</p>
            <p className="text-[var(--steel)] text-sm mt-1 font-mono">Response window · 24–48 hrs</p>
          </div>
        </div>

        <form onSubmit={onSubmit} className="md:col-span-8 space-y-6" data-testid="contact-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <label className="eyebrow block mb-2">Full Name *</label>
              <input data-testid="contact-full-name" required value={form.full_name} onChange={set("full_name")} className={inputCls} placeholder="Jane Doe" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Company / Organization</label>
              <input data-testid="contact-organization" value={form.organization} onChange={set("organization")} className={inputCls} placeholder="Company or institution" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Email Address *</label>
              <input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} placeholder="jane@company.com" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Phone Number</label>
              <input data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="+1 000 000 0000" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Country</label>
              <input data-testid="contact-country" value={form.country} onChange={set("country")} className={inputCls} placeholder="Country" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Subject *</label>
              <input data-testid="contact-subject" required value={form.subject} onChange={set("subject")} className={inputCls} placeholder="Nature of enquiry" />
            </div>
          </div>

          <div>
            <label className="eyebrow block mb-2">Message *</label>
            <textarea data-testid="contact-message" required rows={5} value={form.message} onChange={set("message")} className={inputCls + " resize-none"} placeholder="Describe your program, questions, or partnership interest." />
          </div>

          <div className="pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <p className="text-xs text-[var(--steel)] uppercase tracking-[0.15em] max-w-md">
              By submitting, you agree to Aurelius Dynamics' privacy policy. We never share enquiry information.
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="group inline-flex items-center justify-center gap-3 bg-[var(--navy)] text-white px-10 py-4 text-sm uppercase tracking-[0.2em] hover:bg-[var(--gold-soft)] transition-colors duration-500 disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Submit Enquiry"}
              <ArrowUpRight size={16} className="transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </button>
          </div>
        </form>
      </div>
    </section>
  );
}

function EnquiryTypes() {
  return (
    <section className="py-16 md:py-24 bg-[var(--paper)]" data-testid="contact-enquiries">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4"><p className="eyebrow-gold">§ 04 — Direct Enquiries</p></div>
          <div className="md:col-span-8">
            <FadeUp><h2 className="h-section text-4xl md:text-5xl text-[var(--navy)]">Reach the right team.</h2></FadeUp>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--line)] border border-[var(--line)]">
          {ENQUIRIES.map((en, i) => (
            <FadeUp key={en.title} delay={i * 0.05} className="bg-white p-8 min-h-[180px] flex flex-col justify-between group">
              <p className="font-serif text-2xl text-[var(--navy)]">{en.title}</p>
              <a href={`mailto:${en.email}`} className="mt-6 text-sm text-[var(--steel)] link-underline group-hover:text-[var(--navy)]">
                {en.email}
              </a>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function MapAndSocials() {
  const src = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d13878.13!2d-95.365!3d29.760!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjnCsDQ1JzM2LjAiTiA5NcKwMjEnNTQuMCJX!5e0!3m2!1sen!2sus";
  return (
    <section className="py-16 md:py-24" data-testid="contact-map">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-8">
          <p className="eyebrow-gold mb-6">§ 05 — Headquarters Location</p>
          <FadeUp>
            <div className="relative aspect-[16/10] overflow-hidden border border-[var(--line)]">
              <iframe
                title="Aurelius Dynamics HQ"
                src={src}
                className="absolute inset-0 w-full h-full grayscale"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute top-4 left-4 bg-white/95 px-4 py-3 border-l-2 border-[var(--gold)]">
                <p className="eyebrow-gold text-[10px]">Global HQ</p>
                <p className="font-serif text-lg text-[var(--navy)] mt-1">1200 Innovation Parkway</p>
              </div>
            </div>
          </FadeUp>
        </div>
        <div className="md:col-span-4 flex flex-col justify-between">
          <FadeUp delay={0.15}>
            <p className="eyebrow-gold mb-6">Follow</p>
            <div className="flex flex-col gap-3">
              {[
                { name: "LinkedIn", icon: Linkedin, href: "#", testid: "social-linkedin" },
                { name: "X (Twitter)", icon: null, href: "#", testid: "social-x" },
                { name: "YouTube", icon: Youtube, href: "#", testid: "social-youtube" },
              ].map((s) => (
                <a
                  key={s.name}
                  href={s.href}
                  data-testid={s.testid}
                  className="group flex items-center justify-between border-b border-[var(--line)] py-4 hover:bg-[var(--paper)] px-2 -mx-2 transition-colors duration-300"
                >
                  <span className="flex items-center gap-4 text-[var(--navy)] font-serif text-2xl">
                    {s.icon ? <s.icon size={18} /> : <span className="font-serif italic text-xl">X</span>}
                    {s.name}
                  </span>
                  <ArrowUpRight size={14} className="text-[var(--steel)] transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </a>
              ))}
            </div>
          </FadeUp>

          <FadeUp delay={0.3} className="mt-10 md:mt-0 pt-8 border-t border-[var(--line)]">
            <p className="eyebrow mb-2">In an emergency</p>
            <p className="text-[var(--navy)] font-serif text-xl">+1 (713) 555-0100</p>
            <p className="text-[var(--steel)] text-xs mt-2 font-mono">24 / 7 · Global Operations Desk</p>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}

export default function Contact() {
  return (
    <div>
      <PageHeader
        eyebrow="§ Contact"
        title={["Speak with", "Aurelius Dynamics."]}
        description="For partnerships, research collaborations, and engineering solutions — a member of our team will respond personally."
        meta={<span>Chapter III / IV</span>}
      />
      <ContactHQ />
      <RegionalContacts />
      <ContactForm />
      <EnquiryTypes />
      <MapAndSocials />
    </div>
  );
}
